import React from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import ScopedCssBaseline from '../StyleBase/ScopedCssBaseline';
import config, { renderMediaQueries, resolveAlignItems, resolveJustifyContent } from './config';
import { viewports } from '../helper';

const resolveMargin = () => renderMediaQueries(dimension => {
  const gutterWidth = parseFloat(config.gutterWidth[dimension]) / 2;
  return css(["margin-left:-", "px;margin-right:-", "px;"], gutterWidth, gutterWidth);
});

const resolveReverse = reverse => {
  if (!reverse) return;

  if (Array.isArray(reverse)) {
    return renderMediaQueries(dimension => css(["flex-direction:", ";"], reverse.indexOf(dimension) > -1 ? 'row-reverse' : 'row'));
  }

  return css(["flex-direction:row-reverse;"]);
};

const Root = styled.div.withConfig({
  displayName: "GridRow__Root",
  componentId: "sc-1y9oihu-0"
})(["", " display:flex;flex:1 0 auto;flex-wrap:wrap;", " ", " ", " ", ""], ScopedCssBaseline, resolveMargin(), _ref => {
  let {
    reverse
  } = _ref;
  return resolveReverse(reverse);
}, _ref2 => {
  let {
    align
  } = _ref2;
  return resolveAlignItems(align);
}, _ref3 => {
  let {
    justify
  } = _ref3;
  return resolveJustifyContent(justify);
});

const GridRow = props => /*#__PURE__*/React.createElement(Root, props);

GridRow.viewports = {
  smallMobile: viewports.smallMobile,
  mobile: viewports.mobile,
  tablet: viewports.tablet,
  desktop: viewports.desktop,
  largeDesktop: viewports.largeDesktop
};
GridRow.defaultProps = {
  reverse: [],
  align: null,
  justify: null
};
process.env.NODE_ENV !== "production" ? GridRow.propTypes = {
  /**
   * Reverses the direction of the row.
   *
   * @example
   * // Can be an array. Reverses for only included viewports.
   * <Grid.Row reverse={[Grid.viewports.mobile, Grid.viewports.desktop]}></Grid.Row>
   *
   * @example
   * // When is a boolean, reverses for all breakpoints.
   * <Grid.Row reverse></Grid.Row>
   */
  reverse: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),

  /**
   * Align the content vertically.
   *
   * You can use all CSS `align-items` values:
   * https://developer.mozilla.org/en-US/docs/Web/CSS/align-items
   *
   * @example
   * // Can be a string:
   * <Grid.Row align="flex-end"></Grid.Row>
   *
   * @example
   * // Or an object:
   * <Grid.Row align={{smallMobile: 'center', desktop: 'flex-end'}}></Grid.Row>
   *
   */
  align: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),

  /**
   * Align the content horizontally.
   *
   * You can use all CSS `justify-content` values:
   * https://developer.mozilla.org/en-US/docs/Web/CSS/justify-content
   *
   * @example
   * // Can be a string:
   * <Grid.Row justify="center"></Grid.Row>
   *
   * @example
   * // Or an object:
   * <Grid.Row justify={{smallMobile: 'center', desktop: 'flex-end'}}></Grid.Row>
   *
   */
  justify: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),

  /**
   * Sets children.
   */
  children: PropTypes.node
} : void 0;
export default GridRow;